import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '@chakra-ui/react'; // Import useToast
import { getApiBaseUrl } from '../utils/environment';
import { getBackendConfig } from '../utils/backendConfig';
import { apiClient } from '../services/api/apiClient';
// Added 2026-09-21 (PRD001A 001A.7a correction): centralized bearer-transport
// helper. See src/services/api/authTransport.ts module header for the full
// cross-site-cookie rationale — bearer is sent ALONGSIDE the session cookie,
// never used to decide authStatus by itself.
import { authorizedFetch, clearStoredTokens } from '../services/api/authTransport';
import { legalAcceptanceService, LegalAcceptanceResponse } from '../services/api/LegalAcceptanceService';
// Branch on typed server status; error text never establishes session or consent.
import { ApiError } from '../services/api/ApiError';
import { TERMS_VERSION, PRIVACY_VERSION } from '../config/legal';

// Campaign link from /api/auth/me
export interface CampaignLink {
  campaign_id: string;
  campaign_name: string;
  role: 'player' | 'gm' | 'spectator';
  status: 'active' | 'archived';
}

// Define the expected structure of user information from the backend
export interface UserInfo {
  id: string;
  username: string;
  avatar?: string | null;
  roles?: string[];
  // Screen name management fields
  screenName?: string | null;
  screenNameId?: string | null;
  screenNameTag?: string | null;
  needsScreenName?: boolean;
  canChangeScreenName?: boolean;
  screenNameChangeCount?: number;
  // /api/auth/me response fields
  discord_id?: string | null;
  role?: 'player' | 'gm' | 'admin';
  tier?: 'free' | 'premium' | 'admin';
  campaign_links?: CampaignLink[];
  // Add other relevant fields returned by your /api/auth/session endpoint
}

// Configuration loaded from environment variables (non-sensitive)
export interface ClientConfig {
  discordClientId: string | null;
  googleClientId: string | null;
  isLoaded: boolean;
}

/**
 * Overall authentication status, distinct from the boolean `isAuthenticated`.
 *
 * WHY a fourth state (`unavailable`) exists, per PRD001A 001A.7a: a session
 * check can fail for two very different reasons — the user is genuinely not
 * signed in (401 / `authenticated: false`), or the auth store/backend itself
 * could not be reached (503, other 5xx, network failure). Collapsing both
 * into "not authenticated" makes an outage look like a sign-out, which is
 * exactly the failure mode this PRD requires the frontend to stop doing.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'unavailable';

/**
 * `pending` is unresolved, `unauthenticated` means a legal endpoint rejected
 * the session and triggered session revalidation, `forbidden` is a distinct 403,
 * `unavailable` is a transport/server failure, and `update-required` means
 * server policy versions do not match bundled text.
 * Only `accepted` may satisfy callers that gate access on consent.
 */
export type LegalAcceptanceStatus = 'pending' | 'accepted' | 'required' | 'unauthenticated' | 'forbidden' | 'unavailable' | 'update-required';

// Define the shape of the context
interface AuthContextType {
  /** Structured status — prefer this over `isAuthenticated`/`isLoading` for new code. */
  authStatus: AuthStatus;
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  /**
   * Whether the best-effort profile enrichment (`enrichProfile` — the call
   * that resolves `userInfo.needsScreenName`) has SETTLED for the current
   * `authStatus === 'authenticated'` session: it succeeded, or it failed in
   * a way `enrichProfile` has already logged and moved on from. `false`
   * while it's still in flight (PRD001A R7): a consumer of
   * `userInfo?.needsScreenName` should treat `undefined` while this is
   * `false` as "not yet known — wait", never as "false" (onboarding
   * complete). Once `true`, an `undefined` `needsScreenName` means
   * enrichment could not determine it (e.g. `/auth/me` degraded) rather
   * than "still resolving" — callers must block eligibility-dependent actions
   * until another server response resolves the prerequisite.
   * Reset to `false` at the start of every `checkSessionStatus()` call.
   */
  profileEnrichmentSettled: boolean;
  /** Server-backed legal status; unknown or unavailable never means accepted. */
  legalAcceptance: LegalAcceptanceResponse | null;
  legalAcceptanceStatus: LegalAcceptanceStatus;
  refreshLegalAcceptance: () => Promise<void>;
  acceptLegalAcceptance: () => Promise<void>;
  clientConfig: ClientConfig;
  isLoading: boolean; // Added to track initial session check
  /**
   * Re-run the session check. Serves double duty as the initial check, the
   * post-OAuth-callback re-check, and the manual "Try again" action from
   * `ServiceUnavailable` when `authStatus === 'unavailable'`. Calling this
   * always resets the automatic-retry backoff counter (see module docs below).
   */
  checkSessionStatus: () => Promise<void>;
  logout: () => Promise<void>;
  isClientAvailable: (type: 'discord' | 'google') => boolean;
  // Screen name management functions
  createScreenName: (screenName: string) => Promise<void>;
  updateScreenName: (screenName: string) => Promise<void>;
  checkScreenNameAvailability: (screenName: string) => Promise<{
    available: boolean;
    valid: boolean;
    errors: string[];
    suggestions: string[];
    existing_count?: number;
    info_message?: string | null;
  }>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  authStatus: 'loading',
  isAuthenticated: false,
  userInfo: null,
  profileEnrichmentSettled: false,
  legalAcceptance: null,
  legalAcceptanceStatus: 'pending',
  refreshLegalAcceptance: async () => {},
  acceptLegalAcceptance: async () => {},
  clientConfig: {
    discordClientId: null,
    googleClientId: null,
    isLoaded: false
  },
  isLoading: true, // Start loading initially
  checkSessionStatus: async () => {},
  logout: async () => {},
  isClientAvailable: () => false,
  // Screen name management defaults
  createScreenName: async () => {},
  updateScreenName: async () => {},
  checkScreenNameAvailability: async () => ({ available: false, valid: false, errors: [], suggestions: [] })
});

// Hook for using the auth context (renamed for clarity)
export const useAuth = () => useContext(AuthContext);

/**
 * Pending-invite return path (PRD001A 001A.6, item 3).
 *
 * WHO: `InviteLanding.tsx` (writes the pending token before sending the user
 * to `/login`), `Login.tsx`/`Dashboard.tsx` (the two pages reachable right
 * after a login/OAuth round-trip — call `useInviteReturnPath()`).
 * WHAT: A same-origin-only client HINT that a user was mid-invite when they
 * had to go log in. `sessionStorage` (not `localStorage`): tab-scoped,
 * cleared when the tab closes, survives the OAuth provider round-trip
 * (same tab, full-page navigation away and back) — never sent anywhere,
 * never itself a credential.
 * WHY: The backend's OAuth callback always redirects to one FIXED
 * `frontend_redirect_url` (src/auth/config.py, not caller-supplied) — there
 * is no `state`/`return_to` param threaded through the provider round-trip.
 * The only way to "resume" the invite after login is this frontend-only
 * hint, resolved on whichever page the user lands on post-login.
 * SECURITY: Stored client state alone never grants admission to a
 * campaign — it only decides where the browser navigates next. The actual
 * join call re-validates the token and membership against the database on
 * every attempt (see `CampaignService.joinCampaignWithToken` /
 * `CampaignService.getInvitePreview`); a stale or tampered value here can,
 * at worst, send the user to `/invite/:token`, which itself performs no
 * mutation until they click Join. The token is never logged.
 * The pending invite stays in sessionStorage while the user completes
 * screen-name setup and server-recorded legal acceptance. It is consumed
 * only after AuthContext has verified all onboarding eligibility signals.
 */
const PENDING_INVITE_TOKEN_KEY = 'mythbound_pending_invite_token';

export function setPendingInviteToken(token: string): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
  } catch {
    // Storage unavailable (private mode, quota, etc.) — the login flow
    // still works, it just won't auto-return to the invite afterward.
  }
}

export function getPendingInviteToken(): string | null {
  try {
    return sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearPendingInviteToken(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    // Nothing to clean up if storage isn't available.
  }
}

/**
 * Onboarding eligibility, derived from `userInfo.needsScreenName` and
 * `profileEnrichmentSettled` (PRD001A R7a correction, 2026-09-23).
 *
 * WHY this exists as an explicit, single derivation instead of letting each
 * call site read the two raw flags itself: `needsScreenName === undefined`
 * is genuinely ambiguous, and collapsing it down to a boolean-ish check is
 * exactly how the R7a defect happened. The R7 fix already made
 * "unknown-while-still-in-flight" explicit (wait). It left a SECOND,
 * different case falling through the same `if (needsScreenName)` truthiness
 * check as "does not need a screen name": `needsScreenName` still
 * `undefined` AFTER `profileEnrichmentSettled` becomes `true` — i.e.
 * `/auth/me` finished (returned a non-OK response, or threw) WITHOUT ever
 * producing a verified answer. A finished request is not a resolved
 * eligibility answer. That fallthrough let an ordinary membership pre-check
 * 403 enable Join before onboarding status was actually known. This
 * function makes all four states explicit so no caller can reintroduce that
 * fallthrough by accident:
 *   - `'unknown'`                    — enrichment is still in flight (or has
 *     not started for this session yet). Wait; do not guess either way.
 *   - `'resolved-eligible'`          — a verified `/auth/me` response (the
 *     profile-enrichment source — see `enrichProfile`) confirmed a real
 *     screen name exists. `needsScreenName` is only ever set to `false` on
 *     that success path, never inferred from anything else (in particular,
 *     never from `/auth/session`'s `screen_name` field, which the backend
 *     always populates with a placeholder/fallback value even when the user
 *     has no real screen name — that field's contract does not support this
 *     decision).
 *   - `'resolved-needs-screen-name'` — the same verified response confirmed
 *     no screen name exists yet.
 *   - `'unresolved'`                 — enrichment settled (definitively
 *     finished trying) without ever reaching either verified outcome above
 *     (a non-OK `/auth/me` response, a thrown error, or an unparsable body).
 *     This must NEVER be treated as either verdict — callers block forward
 *     progress and surface a distinct, retryable state instead of guessing.
 *     It recovers the moment a fresh check produces a definitive answer
 *     (e.g. a manual retry, or the next `checkSessionStatus()` call).
 */
export type OnboardingEligibility =
  | 'unknown'
  | 'resolved-eligible'
  | 'resolved-needs-screen-name'
  | 'unresolved';

/**
 * Distinguish unresolved enrichment from a confirmed negative answer. Invite
 * and login-return callers may proceed only on `resolved-eligible`; a request
 * that settled without a usable field remains inconclusive.
 */
export function deriveOnboardingEligibility(
  needsScreenName: boolean | undefined,
  profileEnrichmentSettled: boolean
): OnboardingEligibility {
  if (needsScreenName === true) return 'resolved-needs-screen-name';
  if (needsScreenName === false) return 'resolved-eligible';
  // needsScreenName === undefined: distinguish "not resolved yet" from
  // "resolved as best enrichment could, but never got a definitive answer."
  return profileEnrichmentSettled ? 'unresolved' : 'unknown';
}

/**
 * Resumes a pending invite once login AND onboarding (screen name) are both
 * complete. Call from a component rendered inside the Router (e.g.
 * `Login.tsx`, `Dashboard.tsx`) — `AuthProvider` itself renders outside
 * `<Router>` in `App.tsx`, so this cannot live in the provider's own body.
 *
 * Deliberately does nothing while `authStatus` is still `'loading'`, while
 * onboarding eligibility is `'unknown'` or `'unresolved'`, or while it is
 * confirmed `'resolved-needs-screen-name'` — resuming in any of those cases
 * would either drop the user back on the invite page before they can
 * actually join, or (the R7a defect) silently treat a finished-but-
 * inconclusive lookup as a green light. Clears the stored token immediately
 * once it navigates, so a stale hint can't repeatedly redirect on a later,
 * unrelated visit to either page.
 *
 * ONBOARDING-ORDER FIX (PRD001A R7, 2026-09-22): `checkSessionStatus` sets
 * `authStatus = 'authenticated'` as soon as `GET /api/auth/session` confirms
 * a valid session, but `userInfo.needsScreenName` is only ever populated by
 * the SEPARATE, best-effort `enrichProfile()` call it fires afterward (see
 * that function's header) — so there is a real window, right after login,
 * where `authStatus === 'authenticated'` and `userInfo?.needsScreenName` is
 * still `undefined` (profile enrichment hasn't resolved yet, or failed).
 *
 * R7a CORRECTION (2026-09-23): the R7 fix above used
 * `if (userInfo?.needsScreenName === undefined && !profileEnrichmentSettled) return;`
 * followed by `if (userInfo?.needsScreenName) return;` — that still let a
 * settled-but-still-`undefined` result (enrichment gave up without a
 * verified answer) fall through both checks and proceed as if eligibility
 * were confirmed. `deriveOnboardingEligibility` above replaces both checks:
 * only `'resolved-eligible'` proceeds; `'unknown'`, `'unresolved'`, and
 * `'resolved-needs-screen-name'` all wait.
 */
export function useInviteReturnPath(): void {
  const { authStatus, userInfo, profileEnrichmentSettled, legalAcceptance, legalAcceptanceStatus } = useContext(AuthContext);
  const navigate = useNavigate();
  const onboardingEligibility = deriveOnboardingEligibility(userInfo?.needsScreenName, profileEnrichmentSettled);

  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    if (onboardingEligibility !== 'resolved-eligible') return; // unknown/unresolved/needs-screen-name — never guess, wait
    if (legalAcceptanceStatus !== 'accepted' || !legalAcceptance?.eligible) return;
    const token = getPendingInviteToken();
    if (!token) return;
    clearPendingInviteToken();
    // URL normalization treats exact dot segments specially even after
    // encodeURIComponent; discard these invalid hints instead of navigating.
    if (token === '.' || token === '..') return;
    // Storage is only a navigation hint. Keep even a tampered value inside
    // one invite path segment; it cannot inject a path, query or redirect.
    navigate(`/invite/${encodeURIComponent(token)}`, { replace: true });
  }, [authStatus, onboardingEligibility, legalAcceptanceStatus, legalAcceptance?.eligible, navigate]);
}

interface AuthProviderProps {
  children: ReactNode;
}

/** Automatic retry backoff (ms) after an `unavailable` result, before giving up
 * and waiting for a manual "Try again" click. Capped and short so a real outage
 * doesn't turn into a request storm against a struggling backend. */
const AUTO_RETRY_DELAYS_MS = [1000, 3000];

/**
 * AuthProvider — sole source of truth for "is this browser signed in."
 *
 * WHO: Wraps the whole app in `App.tsx`; every `ProtectedRoute` and any
 * component calling `useAuth()` reads `authStatus`/`isAuthenticated` from here.
 * WHAT: Owns `checkSessionStatus` (the single call to the backend session
 * authority, `GET /api/auth/session`), `logout`, OAuth client-ID config
 * loading, and screen-name management passthroughs to `apiClient`.
 * WHERE: src/context/AuthContext.tsx.
 * WHEN: `checkSessionStatus` runs once on mount (after client config loads),
 * again after an OAuth redirect drops tokens in the URL hash, and again on
 * every manual retry from `ServiceUnavailable`.
 * WHY (PRD001A 001A.2 / 001A.7a): the backend is moving to a single,
 * cookie-authoritative session check with no client-side JWT trust. This
 * provider previously fell back through `/api/auth/me` and then a locally
 * decoded, unverified JWT from `localStorage` when the session check failed —
 * confirmed in the 001A.1 inventory as capable of turning a 503/network
 * outage into a false "authenticated" state. That fallback is removed here.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [clientConfig, setClientConfig] = useState<ClientConfig>({
    discordClientId: null,
    googleClientId: null,
    isLoaded: false
  });
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  // See `AuthContextType.profileEnrichmentSettled` header comment (PRD001A R7).
  const [profileEnrichmentSettled, setProfileEnrichmentSettled] = useState(false);
  const [legalAcceptance, setLegalAcceptance] = useState<LegalAcceptanceResponse | null>(null);
  const [legalAcceptanceStatus, setLegalAcceptanceStatus] = useState<LegalAcceptanceStatus>('pending');
  const activeUserIdRef = useRef<string | null>(null);
  const legalRequestGenerationRef = useRef(0);
  // A legal-endpoint 401 is rechecked against session authority. Suppressing the
  // follow-up legal read prevents recursion if that endpoint keeps returning 401.
  const revalidateSessionAfterLegalUnauthorizedRef = useRef<() => void>(() => {});
  const toast = useToast(); // Initialize toast

  // In-flight-request cancellation: a new checkSessionStatus() call (manual
  // retry, re-check after OAuth) must not race a stale one. AbortController
  // lets us cancel the previous request rather than just ignoring its result,
  // so a slow, superseded response can never overwrite fresher state.
  const abortControllerRef = useRef<AbortController | null>(null);
  // Bounded automatic-retry state for the `unavailable` outcome. A manual call
  // to checkSessionStatus() (retry button, OAuth re-check) always resets this
  // to 0, so the user is never permanently locked out of automatic retries.
  const autoRetryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the server response intact, then derive UI status against the versions
  // this browser can actually show; a mismatch must block acceptance.
  const applyLegalAcceptance = useCallback((response: LegalAcceptanceResponse) => {
    setLegalAcceptance(response);
    const matchesVisiblePolicies = response.requiredTermsVersion === TERMS_VERSION
      && response.requiredPrivacyVersion === PRIVACY_VERSION;
    setLegalAcceptanceStatus(!matchesVisiblePolicies ? 'update-required' : response.accepted ? 'accepted' : 'required');
  }, []);

  // Advancing the generation invalidates any response already in flight when
  // logout or a session transition removes the active user.
  const invalidateLegalAcceptance = useCallback(() => {
    legalRequestGenerationRef.current += 1;
    activeUserIdRef.current = null;
    setLegalAcceptance(null);
    setLegalAcceptanceStatus('pending');
  }, []);

  // Each status read is tied to both its initiating user and generation, so a
  // late response cannot overwrite newer status or leak across account changes.
  const loadLegalAcceptance = useCallback(async (userId: string, signal?: AbortSignal) => {
    if (activeUserIdRef.current !== userId) return;
    const generation = ++legalRequestGenerationRef.current;
    setLegalAcceptance(null);
    setLegalAcceptanceStatus('pending');
    try {
      const response = await legalAcceptanceService.getStatus(signal);
      if (generation !== legalRequestGenerationRef.current || activeUserIdRef.current !== userId) return;
      applyLegalAcceptance(response);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (generation !== legalRequestGenerationRef.current || activeUserIdRef.current !== userId) return;
      setLegalAcceptance(null);
      if (error instanceof ApiError && error.kind === 'unauthenticated') {
        revalidateSessionAfterLegalUnauthorizedRef.current();
        return;
      }
      console.warn('[AuthProvider] Legal acceptance status failed:', error);
      setLegalAcceptanceStatus(error instanceof ApiError && error.kind === 'forbidden' ? 'forbidden' : 'unavailable');
    }
  }, [applyLegalAcceptance]);

  const refreshLegalAcceptance = useCallback(async () => {
    const userId = activeUserIdRef.current;
    if (!userId) {
      setLegalAcceptance(null);
      setLegalAcceptanceStatus('pending');
      return;
    }
    await loadLegalAcceptance(userId);
  }, [loadLegalAcceptance]);

  const acceptLegalAcceptance = useCallback(async () => {
    const userId = activeUserIdRef.current;
    if (!userId) throw new Error('A valid session is required to accept the policies.');
    const generation = ++legalRequestGenerationRef.current;
    setLegalAcceptance(null);
    setLegalAcceptanceStatus('pending');
    try {
      // A policy-version conflict or transport failure remains an error; refresh
      // can establish a new state, but the click itself is never optimistic consent.
      const response = await legalAcceptanceService.accept(TERMS_VERSION, PRIVACY_VERSION);
      if (generation !== legalRequestGenerationRef.current || activeUserIdRef.current !== userId) return;
      applyLegalAcceptance(response);
    } catch (error) {
      if (generation === legalRequestGenerationRef.current && activeUserIdRef.current === userId) {
        setLegalAcceptance(null);
        if (error instanceof ApiError && error.kind === 'unauthenticated') {
          revalidateSessionAfterLegalUnauthorizedRef.current();
        } else {
          setLegalAcceptanceStatus(error instanceof ApiError && error.kind === 'forbidden' ? 'forbidden' : 'unavailable');
        }
      }
      throw error;
    }
  }, [applyLegalAcceptance]);

  const clearScheduledRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Load client configuration from backend API
  const loadClientConfig = useCallback(async () => {
    console.log('[AuthProvider] Loading client configuration from backend...');
    try {
      const backendConfig = await getBackendConfig();
      const newConfig = {
        discordClientId: backendConfig.discordClientId,
        googleClientId: backendConfig.googleClientId,
        isLoaded: true
      };
      setClientConfig(newConfig);
      console.log('[AuthProvider] Client configuration loaded from backend:', {
          discord: newConfig.discordClientId ? 'OK' : 'MISSING',
          google: newConfig.googleClientId ? 'OK' : 'MISSING',
      });
    } catch (error) {
      console.error('[AuthProvider] Failed to load client configuration from backend:', error);
      // Fallback to environment variables for development
      const fallbackConfig = {
        discordClientId: process.env.REACT_APP_DISCORD_CLIENT_ID || null,
        googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || null,
        isLoaded: true
      };
      setClientConfig(fallbackConfig);
      console.warn('[AuthProvider] Using fallback client configuration:', {
          discord: fallbackConfig.discordClientId ? 'OK' : 'MISSING',
          google: fallbackConfig.googleClientId ? 'OK' : 'MISSING',
      });
    }
  }, []);

  /**
   * Best-effort profile enrichment after a confirmed-authenticated session.
   *
   * WHY this exists separately from checkSessionStatus: `GET /api/auth/session`
   * is the lightweight, authoritative signed-in/out check. `GET /api/auth/me`
   * returns a richer profile (roles, tier, campaign_links) that several pages
   * read (Layout, Profile, RoleBasedAccess) but that is not needed to answer
   * "is this user signed in." Calling it here with authorizedFetch, after tier 1
   * already confirmed authenticated, means its failure is a degraded-profile
   * case — not an auth decision. It must never flip `authStatus`: an outage on
   * this optional call is not a sign-out and not an outage of the auth check
   * itself (which already succeeded).
   *
   * Uses `authorizedFetch` (cookie + bearer-if-fresh, per the cross-site
   * transport rule below) like every other authenticated call — but its
   * *result* still never feeds the authenticated/unauthenticated decision.
   *
   * SETTLEMENT TRACKING (PRD001A R7): sets `profileEnrichmentSettled = true`
   * once this call has genuinely finished — success, or a definitive
   * failure it has already logged and moved on from — so a consumer of
   * `userInfo?.needsScreenName` (see `useInviteReturnPath`/`InviteLanding`)
   * can tell "not resolved yet" (`false`, still in flight) apart from
   * "resolved as best it could, still unknown" (`true` with `needsScreenName`
   * still `undefined`). Deliberately NOT set on `AbortError`: an aborted
   * call was superseded by a newer `checkSessionStatus()`, which already
   * reset this flag to `false` for ITS OWN in-flight enrichment — letting
   * the old, cancelled call mark it settled here could otherwise race
   * ahead of the new one's real result.
   */
  const enrichProfile = useCallback(async (userId: string, signal: AbortSignal) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const meResponse = await authorizedFetch(`${apiBaseUrl}/auth/me`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (signal.aborted || activeUserIdRef.current !== userId) return;
      if (!meResponse.ok) {
        console.warn(`[AuthProvider] Profile enrichment via /auth/me returned ${meResponse.status}; keeping minimal session profile`);
        setProfileEnrichmentSettled(true);
        return;
      }
      const profile = await meResponse.json();
      if (signal.aborted || activeUserIdRef.current !== userId) return;
      setUserInfo((prev) =>
        prev?.id === userId
          ? {
              ...prev,
              username: profile.screenname || prev.username,
              roles: profile.role ? [profile.role] : prev.roles,
              screenName: profile.screenname ?? prev.screenName,
              needsScreenName: !profile.screenname,
              canChangeScreenName: true,
              discord_id: profile.discord_id ?? prev.discord_id,
              role: profile.role ?? prev.role,
              tier: profile.tier ?? prev.tier,
              campaign_links: profile.campaign_links ?? prev.campaign_links,
            }
          : prev
      );
      setProfileEnrichmentSettled(true);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (signal.aborted || activeUserIdRef.current !== userId) return;
      // Non-fatal: the user is still authenticated per the session check that
      // preceded this call. Log and move on with the minimal profile.
      console.warn('[AuthProvider] Profile enrichment via /auth/me failed (non-fatal):', err);
      setProfileEnrichmentSettled(true);
    }
  }, []);

  /**
   * Check whether the browser has a valid backend session.
   *
   * Single call path: `GET /api/auth/session` via `authorizedFetch` (cookie
   * always, plus a bearer header when a non-expired token is on hand — see
   * `authTransport.ts`). That bearer is a *transport* choice only: the
   * authenticated/unauthenticated decision below reads exclusively the JSON
   * body of this response, never anything decoded from the token itself.
   *
   * Status mapping (PRD001A 001A.7a, binding):
   * - 200 with `authenticated: true` → `authStatus = 'authenticated'`.
   * - 401, or 200 with `authenticated: false` → `authStatus = 'unauthenticated'`,
   *   and any stored bearer tokens are cleared (they didn't establish
   *   identity, and holding onto a rejected token serves no purpose). An
   *   ordinary signed-out user is not an outage.
   * - Any other non-ok status (5xx etc.) or a thrown network error →
   *   `authStatus = 'unavailable'`. Never signed out, never authenticated.
   *   Stored tokens are left alone — an outage says nothing about whether
   *   they're still valid.
   *
   * No local JWT decode anywhere in this function — removed per PRD001A
   * 001A.7a after the 001A.1 inventory found the previous three-tier
   * fallback could authenticate a user purely from a stale client-held
   * token with no server round-trip. `authorizedFetch` already handles one
   * refresh-and-retry internally for a `401 { code: 'token_expired' }`
   * response, so by the time this function sees a 401 here, that retry has
   * already happened and failed.
   *
   * @param isAutoRetry Internal flag set only by the bounded auto-retry timer
   *   below; a caller-initiated call (mount, manual retry, OAuth re-check) is
   *   always `false` and resets the retry counter.
   * @param skipLegalStatusRead Internal guard for session revalidation after a
   *   legal endpoint 401; it prevents an immediate recursive read of the same endpoint.
   */
  const checkSessionStatus = useCallback(async (isAutoRetry: boolean = false, skipLegalStatusRead: boolean = false): Promise<void> => {
    // Cancel any previous in-flight check so a late response from a superseded
    // request can never overwrite the result of a newer one.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    clearScheduledRetry();

    if (!isAutoRetry) {
      autoRetryCountRef.current = 0;
    }

    console.log('[AuthProvider] Checking authentication status...');
    invalidateLegalAcceptance();
    setAuthStatus('loading');
    // Every fresh check starts a new enrichment lifecycle — see
    // `profileEnrichmentSettled`'s header comment (PRD001A R7).
    setProfileEnrichmentSettled(false);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await authorizedFetch(`${apiBaseUrl}/auth/session`, {
        method: 'GET',
        signal: controller.signal,
      });

      if (response.status === 401) {
        console.log('[AuthProvider] Not authenticated (401).');
        clearStoredTokens();
        setUserInfo(null);
        invalidateLegalAcceptance();
        setAuthStatus('unauthenticated');
        return;
      }

      if (response.ok) {
        const sessionData = await response.json();
        if (!sessionData.authenticated) {
          console.log('[AuthProvider] Session check ok, authenticated: false.');
          setUserInfo(null);
          invalidateLegalAcceptance();
          setAuthStatus('unauthenticated');
          return;
        }

        const userInfoFromSession: UserInfo = {
          id: sessionData.user_id,
          username: sessionData.screen_name || sessionData.user_id,
          screenName: sessionData.screen_name ?? null,
          tier: sessionData.tier,
          role: sessionData.role,
        };
        console.log('[AuthProvider] Authenticated via /auth/session:', String(sessionData.user_id).substring(0, 12) + '***');
        activeUserIdRef.current = userInfoFromSession.id;
        setUserInfo(userInfoFromSession);
        setAuthStatus('authenticated');
        // Profile enrichment failures never change authentication; legal reads
        // classify 401 for session revalidation and retain 403/other failures as
        // distinct policy-access states without inferring consent.
        void enrichProfile(userInfoFromSession.id, controller.signal);
        if (skipLegalStatusRead) {
          // A 401 from legal status remains a recovery state even if the session
          // endpoint still accepts this account; do not loop into another legal read.
          setLegalAcceptanceStatus('unauthenticated');
        } else {
          void loadLegalAcceptance(userInfoFromSession.id, controller.signal);
        }
        return;
      }

      // Any other status (5xx, or an unexpected 4xx from this endpoint) is an
      // auth-store/backend problem, not a sign-out — surface it as unavailable
      // rather than guessing.
      throw new Error(`Unexpected /auth/session status: ${response.status}`);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Superseded by a newer check; that check owns the state update.
        return;
      }
      console.error('[AuthProvider] Auth store unavailable:', error);
      invalidateLegalAcceptance();
      setAuthStatus('unavailable');

      if (autoRetryCountRef.current < AUTO_RETRY_DELAYS_MS.length) {
        const delay = AUTO_RETRY_DELAYS_MS[autoRetryCountRef.current];
        autoRetryCountRef.current += 1;
        retryTimeoutRef.current = setTimeout(() => {
          void checkSessionStatus(true);
        }, delay);
      }
      // Beyond the capped auto-retries, we stop and wait for a manual "Try
      // again" (which calls this function with isAutoRetry=false and resets
      // the counter) — this is the "no retry storm" requirement.
    }
  }, [clearScheduledRetry, enrichProfile, invalidateLegalAcceptance, loadLegalAcceptance]);

  // Keep this callback current without creating a dependency cycle in legal request handlers.
  revalidateSessionAfterLegalUnauthorizedRef.current = () => { void checkSessionStatus(false, true); };

  // Cancel any in-flight request and scheduled auto-retry on unmount.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      legalRequestGenerationRef.current += 1;
      activeUserIdRef.current = null;
      clearScheduledRetry();
    };
  }, [clearScheduledRetry]);

  /**
   * Sign out: ask the backend to revoke the session (cookie + bearer, via
   * `authorizedFetch`, per the cross-site transport rule), then always clear
   * local state regardless of whether that call succeeded.
   *
   * PRD001A 001A.7a: if the server call fails, we still clear local state
   * (the user should not appear "stuck" signed in), but we do not claim the
   * server session was revoked — the warning toast below is the honest
   * version of that, instead of silently treating a failed revoke as success.
   */
  const logout = useCallback(async () => {
    console.log('[AuthProvider] Logging out...');
    invalidateLegalAcceptance();
    let serverConfirmedLogout = false;

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await authorizedFetch(`${apiBaseUrl}/auth/logout`, {
        method: 'POST',
      });
      serverConfirmedLogout = response.ok;
      if (!response.ok) {
        console.warn(`[AuthProvider] Server logout returned ${response.status}; clearing local state only`);
      }
    } catch (error) {
      console.warn('[AuthProvider] Server logout request failed; clearing local state only:', error);
    } finally {
      // Always clear stored bearer tokens plus the legacy session_id key,
      // regardless of whether the server call above succeeded — a failed
      // revoke must not leave a still-usable bearer sitting in storage.
      clearStoredTokens();
      localStorage.removeItem('session_id'); // Legacy

      setUserInfo(null);
      setAuthStatus('unauthenticated');

      if (!serverConfirmedLogout) {
        toast({
          title: 'Signed out locally',
          description: "We couldn't confirm the server session was revoked. If you're on a shared device, please close the browser.",
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
      }

      console.log('[AuthProvider] Logout complete. Server session revoked:', serverConfirmedLogout);
    }
  }, [toast, invalidateLegalAcceptance]);

  // Check if a specific OAuth client ID is available
  const isClientAvailable = useCallback((type: 'discord' | 'google'): boolean => {
    if (!clientConfig.isLoaded) {
      console.warn(`[AuthProvider] Client config not loaded yet when checking ${type}`);
      return false;
    }
    switch(type) {
      case 'discord':
        return clientConfig.discordClientId !== null;
      case 'google':
        return clientConfig.googleClientId !== null;
      default:
        return false;
    }
  }, [clientConfig.isLoaded, clientConfig.discordClientId, clientConfig.googleClientId]);

  // Screen name management functions
  const createScreenName = useCallback(async (screenName: string) => {
    const userId = activeUserIdRef.current;
    if (!userId) throw new Error('A valid session is required to create a screen name.');
    try {
      // Use apiClient for JWT authentication and automatic token refresh
      const result = await apiClient.post('/users/screen-name', {
        screen_name: screenName
      });

      // Ignore a completion from an account that is no longer active.
      if (activeUserIdRef.current !== userId) return;
      setUserInfo(prev => prev?.id === userId ? {
        ...prev,
        screenName: result.screen_name,
        screenNameId: result.screen_name_id,
        screenNameTag: result.screen_name_id?.slice(-4) || null,
        needsScreenName: false,
        canChangeScreenName: result.can_change
      } : prev);

      toast({
        title: 'Screen Name Created',
        description: `Welcome, ${result.screen_name}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await loadLegalAcceptance(userId);
      console.log('[AuthProvider] Screen name created:', result.screen_name);
    } catch (error: any) {
      if (activeUserIdRef.current !== userId) return;
      const collisionTag = error?.detail?.collision_tag || error?.detail?.detail?.collision_tag;
      console.error('[AuthProvider] Error creating screen name:', error);
      if (!collisionTag && activeUserIdRef.current === userId) {
        toast({
          title: 'Screen Name Creation Failed',
          description: error.message || 'Could not create screen name',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      throw error;
    }
  }, [toast, loadLegalAcceptance]);

  const updateScreenName = useCallback(async (newScreenName: string) => {
    const userId = activeUserIdRef.current;
    if (!userId) throw new Error('A valid session is required to update a screen name.');
    try {
      // Use apiClient for JWT authentication and automatic token refresh
      const result = await apiClient.put('/users/screen-name', {
        new_screen_name: newScreenName
      });

      // Ignore a completion from an account that is no longer active.
      if (activeUserIdRef.current !== userId) return;
      setUserInfo(prev => prev?.id === userId ? {
        ...prev,
        screenName: result.screen_name,
        screenNameId: result.screen_name_id,
        screenNameTag: result.screen_name_id?.slice(-4) || null,
        needsScreenName: false,
        canChangeScreenName: result.can_change,
        screenNameChangeCount: (prev.screenNameChangeCount || 0) + 1
      } : prev);

      toast({
        title: 'Screen Name Updated',
        description: `Screen name changed to ${result.screen_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await loadLegalAcceptance(userId);
      console.log('[AuthProvider] Screen name updated:', result.screen_name);
    } catch (error: any) {
      if (activeUserIdRef.current !== userId) return;
      console.error('[AuthProvider] Error updating screen name:', error);
      toast({
        title: 'Screen Name Update Failed',
        description: error.message || 'Could not update screen name',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast, loadLegalAcceptance]);

  const checkScreenNameAvailability = useCallback(async (screenName: string) => {
    try {
      // Use apiClient for JWT authentication and automatic token refresh
      // Note: This endpoint doesn't require authentication, but apiClient provides consistent error handling
      const result = await apiClient.get(`/users/screen-name/check?screen_name=${encodeURIComponent(screenName)}`);
      return result;
    } catch (error) {
      console.error('[AuthProvider] Error checking screen name availability:', error);
      return { available: false, valid: false, errors: ['Unable to check availability'], suggestions: [] };
    }
  }, []);

  // Extract JWT tokens from URL hash (cross-domain OAuth support)
  // OAuth redirect passes JWT tokens in URL fragment:
  // /dashboard#access_token=xxx&refresh_token=yyy&token_type=Bearer&expires_in=3600
  //
  // NOTE: these captured tokens are NOT used for the authenticated/unauthenticated
  // decision above (that is the /api/auth/session response). They are stored only
  // for the still-bearer-based clients that have not yet migrated to cookie-only
  // auth (apiClient.ts; see PRD001A 001A.7a scope note in 001A_FE_Checkpoint.md).
  // After storing them we re-run checkSessionStatus() so authStatus reflects the
  // new cookie session, not the presence of a token in storage.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token=')) {
      console.log('[AuthProvider] Detected JWT tokens in URL hash');

      // Parse URL hash parameters
      const params = new URLSearchParams(hash.substring(1)); // Remove leading #
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const expiresIn = params.get('expires_in');

      if (accessToken && refreshToken) {
        console.log('[AuthProvider] Extracted JWT tokens from URL hash');

        // Store tokens in localStorage
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        // Calculate expiry timestamp (current time + expires_in)
        const expiryTime = Date.now() + (parseInt(expiresIn || '3600', 10) * 1000);
        localStorage.setItem('token_expiry', expiryTime.toString());

        // Remove deprecated session_id if present
        localStorage.removeItem('session_id');

        // Clear hash from URL to avoid exposing tokens
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        // Trigger a fresh, authoritative session check (cookie-based).
        void checkSessionStatus();
      }
    }
  }, [checkSessionStatus]);

  // Initial load: Config first, then session status
  useEffect(() => {
    loadClientConfig().catch(error => {
      console.error('[AuthProvider] Error during config loading:', error);
    });
  }, [loadClientConfig]);

  useEffect(() => {
    if (clientConfig.isLoaded) {
        void checkSessionStatus();
    }
  }, [clientConfig.isLoaded, checkSessionStatus]);

  // Context value provided to children
  const value: AuthContextType = {
    authStatus,
    isAuthenticated: authStatus === 'authenticated',
    userInfo,
    profileEnrichmentSettled,
    legalAcceptance,
    legalAcceptanceStatus,
    refreshLegalAcceptance,
    acceptLegalAcceptance,
    clientConfig,
    isLoading: authStatus === 'loading',
    checkSessionStatus,
    logout,
    isClientAvailable,
    createScreenName,
    updateScreenName,
    checkScreenNameAvailability
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
